import React, { useState, useMemo, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import {
  fetchFlights,
  getFlightNumberHistory,
  getLastFlightNumber,
} from "apis/loadsServices";
import { useFlightNumbers } from "./useFlightNumbers";
import SearchResults from "./SearchResults";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateTime } from "utils/dateUtils";
import {
  formatDateLocal,
  saveLastFlightSearchToStorage,
  loadLastFlightSearchFromStorage,
  LastFlightSearchData,
} from "./searchStorage";
import { withDirectFlightCompositeIds } from "./routeSearchHelpers";
import DateRangeCalendar from "./DateRangeCalendar";
import RoutePager from "./RoutePager";
import MobileStopsFilter from "./MobileStopsFilter";
import { useClickOutside } from "../../hooks/useClickOutside";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
`;

interface SearchFlightsNumberProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: (optimisticItems?: any[]) => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
}



const SearchFlightsNumber: React.FC<SearchFlightsNumberProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const { showToast } = useToastStore.getState();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [showFlightSuggestions, setShowFlightSuggestions] = useState(false);
  const flightFieldRef = useRef<HTMLDivElement | null>(null);
  const [date, setDate] = useState<string>(
    new Date(Date.now() + 86400000).toISOString().split("T")[0]
  ); // Default to Tomorrow's date
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<ConnectionFlight[]>([]);
  const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | "one" | "two" | "three" | null>("none");
  const [isLoadingFlights, setIsLoadingFlights] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    flightNumber?: string;
    date?: string;
    apiResponse?: string;
  }>({});
  const { showLoader, hideLoader } = useGlobalLoader();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Track if we're auto-resetting to prevent infinite loops
  const isAutoResetting = useRef<boolean>(false);
  // Tracks the last page we fetched, so we refetch on ANY page change
  // (including going back to page 1), but not on initial load / search reset.
  const prevPageNumber = useRef<number | null>(null);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [startDate, endDate] = range;
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  // Flight-number autocomplete data: full list fetched once and cached,
  // filtered locally as the user types (no debounce needed).
  const { isLoadingFlightNumbers, filterFlightNumbers } = useFlightNumbers();
  const flightNumberOptions = useMemo(
    () =>
      filterFlightNumbers(flightNumber).map((value) => ({
        value,
        label: value,
      })),
    [filterFlightNumbers, flightNumber],
  );

  // Close the flight-number suggestions when clicking outside the field
  useClickOutside(flightFieldRef, () => setShowFlightSuggestions(false));

  const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => {
    if (!filter) return;
    setStopsFilter(filter);
    // If we have already searched, trigger a new search with the updated filter
    if (hasSearched) {
      getFlights({ stopsFilter: filter });
    }
  };

  const handleLastSearchClick = async () => {
    try {
      // First check localStorage
      let lastSearchData: LastFlightSearchData | null = loadLastFlightSearchFromStorage();
      
      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const { lastSearchFlightNumber } = await getLastFlightNumber();
          if (lastSearchFlightNumber) {
            // Convert UTC date to YYYY-MM-DD format
            const lastSearchDate = new Date(lastSearchFlightNumber.date)
              .toISOString()
              .split("T")[0];
            
            lastSearchData = {
              flightNumber: lastSearchFlightNumber.flightNumber,
              dates: [lastSearchDate],
              savedAt: Date.now(),
            };
            
            // Save to localStorage for future use
            saveLastFlightSearchToStorage(
              lastSearchFlightNumber.flightNumber,
              [lastSearchDate]
            );
          } else {
            hideLoader();
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          return;
        } finally {
          hideLoader();
        }
      }
      
      // Populate fields with stored data
      if (lastSearchData) {
        const { flightNumber, dates } = lastSearchData;

        setFlightNumber(flightNumber);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates.map(dateStr => new Date(dateStr));
        setSelectedDates(dateObjects);
        setDatesInRange(dates);
        
        setErrors({}); // Clear any existing errors

        // Trigger search
        setHasSearched(true);
        onShowSearchResults?.(true);
        prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
        setPageNumber(1);
        // Reset the Stops filter to Non-stop on every fresh search (see note
        // in handleSearchClick).
        setStopsFilter("none");

        try {
          showLoader();
          await getFlights({ flightNumber, dates, pageNumber: 1, stopsFilter: "none" });
          
          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for last search:", error);
        } finally {
          hideLoader();
        }
      }
    } catch (error) {
      console.error("Failed to load last search:", error);
    }
  };

  // Fetch flights when the page actually changes (including back to page 1),
  // but not on initial load or when a new search resets the page.
  useEffect(() => {
    if (
      hasSearched &&
      prevPageNumber.current !== null &&
      prevPageNumber.current !== PageNumber
    ) {
      const fetchFlightsForPage = async () => {
        try {
          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Remember the page we just handled.
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // Auto-reset filter to "none" when "one-or-fewer" returns no valid connection flights
  useEffect(() => {
    if (
      hasSearched &&
      stopsFilter === "one-or-fewer" &&
      !isLoadingFlights &&
      !isAutoResetting.current
    ) {
      // Check if there are any valid connection flights (must have at least 2 segments)
      const hasValidConnections = connectionFlights.some(
        (connection) => connection.segments && connection.segments.length >= 2
      );
      
      // If no valid connection flights and no direct flights, revert to non-stop
      if (!hasValidConnections && flights.length === 0) {
        // Prevent infinite loops by setting the flag
        isAutoResetting.current = true;
        // Reset filter to "none" and trigger a new search
        setStopsFilter("none");
        // Reset the flag after a short delay to allow the filter change to process
        setTimeout(() => {
          isAutoResetting.current = false;
        }, 100);
      }
    }
  }, [flights.length, connectionFlights, stopsFilter, hasSearched, isLoadingFlights]);

  const getFlights = async (overrideParams?: {
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    flightNumber?: string;
    dates?: string[];
    pageNumber?: number;
  }) => {
    setIsLoadingFlights(true);
    try {
      const currentFilter = overrideParams?.stopsFilter ?? stopsFilter;
      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (currentFilter === "none") {
        IncludeDirect = true;
        IncludeConnections = false;
        Stops = 0;
      } else if (currentFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      } else if (currentFilter === "one") {
        Stops = 1;
      } else if (currentFilter === "two") {
        Stops = 2;
      } else if (currentFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only
        IncludeDirect = true;
        IncludeConnections = false;
      }

      const params = {
        FlightNumber: overrideParams?.flightNumber ?? flightNumber,
        Dates: overrideParams?.dates ?? datesInRange,
        PageNumber: overrideParams?.pageNumber ?? PageNumber,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections || []) as ConnectionFlight[];

      // Check if we requested connections but got direct flights instead
      // This happens when API returns direct flights in items array when IncludeConnections: true
      if (currentFilter === "one-or-fewer" && connectionsData.length === 0 && rawFlightData.length > 0) {
        // Check if rawFlightData contains direct flights (stops: 0 or intermediateAirports: [])
        // The API response has 'stops' field in the raw data
        const hasDirectFlights = rawFlightData.some(
          (flight: any) => {
            return (flight.stops === 0 || flight.stops === undefined) ||
                   (flight.intermediateAirports && Array.isArray(flight.intermediateAirports) && flight.intermediateAirports.length === 0);
          }
        );
        
        // If we got direct flights instead of connections, we need to revert to non-stop
        if (hasDirectFlights && !isAutoResetting.current) {
          isAutoResetting.current = true;
          setStopsFilter("none");
          // Don't process this response, let the filter change trigger a new search
          setIsLoadingFlights(false);
          setTimeout(() => {
            isAutoResetting.current = false;
          }, 100);
          return;
        }
      }
      
      const flightData = rawFlightData as FlightDetails[];

      const flightsWithCompositeIds = withDirectFlightCompositeIds(flightData);

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime
            ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
            : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID from ALL segments — two itineraries can share
        // the same first leg and total duration but differ in later legs, so
        // keying on just the first segment made them collide (selecting a shared
        // leg in one card also selected it in the other).
        const connectionCompositeId = segmentsWithIds.length
          ? `conn_${segmentsWithIds.map((s) => s.compositeId).join("_")}`
          : `conn_${connection.totalDuration}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // Total = direct single-flight results (data.total) + multi-leg
      // connections (connectionTotal). A 1/2/3-stop search returns BOTH, so the
      // count is their sum; the missing side counts as 0.
      const directTotal = response.data.total ?? 0;
      const connTotal = response.data.connectionTotal ?? 0;

      if (
        response.data.total !== undefined ||
        response.data.connectionTotal !== undefined
      ) {
        setTotalFlights(directTotal + connTotal);
      } else {
        // Fallback: estimate from this page's results (direct + connections).
        const pageCount = flightData.length + connectionsData.length;
        const estimatedTotal =
          pageCount < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage + pageCount
            : PageNumber * flightsPerPage; // minimum estimate
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      // "No flights found" is a valid empty result, not an error — the inline
      // empty state already communicates it, so don't also raise a red toast.
      const isNoResults =
        status === 404 ||
        (typeof apiMessage === "string" &&
          /no flights?\s+found|no\s+results?/i.test(apiMessage));
      if (!isNoResults) {
        showToast(apiMessage || "Failed to fetch flights.", "error", 3000);
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    const newErrors: typeof errors = {};
    if (!flightNumber) {
      newErrors.flightNumber = "*Flight number is required";
    }

    if (datesInRange.length === 0)
      newErrors.date = "*Please select at least one date.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Only show the search results area when validation passes
      setHasSearched(true);
      onShowSearchResults?.(true);
      prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
      setPageNumber(1); // Reset to first page on new search
      // Every fresh search starts from Non-stop — reset the Stops filter so it
      // doesn't carry over from the previous search. Pass the override too since
      // setStopsFilter is async and getFlights would otherwise read the stale value.
      setStopsFilter("none");
      try {
        // getFlights toggles isLoadingFlights, which drives the button spinner.
        // No global loader here so only the button shows loading (like Route).
        await getFlights({ stopsFilter: "none" });

        // Save to localStorage after successful search
        saveLastFlightSearchToStorage(flightNumber, datesInRange);

        // Scroll to search results after a brief delay to ensure content is rendered
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching flights:", error);
      }
    }
  };

  const handleClearSearch = () => {
    setFlightNumber("");
    setSelectedDates([]);
    setDatesInRange([]);
    setRange([null, null]);
    setFlights([]);
    setConnectionFlights([]);
    setStopsFilter("none");
    setErrors({});
    setHasSearched(false);
    prevPageNumber.current = null; // reset so the next search starts clean
    setPageNumber(1);
    setTotalFlights(0);
    onShowSearchResults?.(false);
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleChange = (update: [Date | null, Date | null]) => {
    setRange(update);
    if (update[0] && update[1]) {
      setDatesInRange(getDatesBetween(update[0], update[1]));
    } else {
      setDatesInRange([]);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime()
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = () => {
    const formattedDates = selectedDates.map((d) => formatDateLocal(d));
    setDatesInRange(formattedDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled (mirrors Search by Route).
    if (flightNumber && selectedDates.length > 0) {
      setTimeout(async () => {
        try {
          setHasSearched(true);
          onShowSearchResults?.(true);
          prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
          setPageNumber(1);
          setErrors({});
          // Reset the Stops filter to Non-stop on every fresh search (see note
          // in handleSearchClick).
          setStopsFilter("none");

          // getFlights toggles isLoadingFlights (button spinner) — no global loader.
          // Pass dates explicitly so we don't depend on the just-set state.
          await getFlights({
            flightNumber,
            dates: formattedDates,
            pageNumber: 1,
            stopsFilter: "none",
          });

          saveLastFlightSearchToStorage(flightNumber, formattedDates);

          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    return (
      <div id="search-results">
        {/* Mobile: back + filter bar */}
        <div className="flex items-center justify-between md:hidden mt-4 mb-2 px-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors"
              onClick={() => {
                handleClearSearch();
                onBack?.();
              }}
            >
              <svg className="w-4 h-4 text-[#1F2937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
            <span className="text-gray-400 text-xs">&gt;</span>
            <span className="text-[#1F2937] text-sm font-medium">Flight No.</span>
          </div>
          <button
            type="button"
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </button>
        </div>

        {/* Mobile: results count */}
        <p className="md:hidden text-[#6B7280] text-xs mb-3 px-1">
          Showing {(Number(totalFlights) || flights.length) + connectionFlights.length} results
        </p>

        {/* Mobile Filter Drawer */}
        <MobileStopsFilter
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          resultCount={
            (Number(totalFlights) || flights.length) + connectionFlights.length
          }
          readOnly={readOnly}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          hideConnectedFilter={true}
          hideThreeStops={true}
        />

        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={(items) => {
            // Return to the home/recent view, then hand the posted cards up.
            handleClearSearch();
            onPostRequestSuccess?.(items);
          }}
          sourceLabel={flightNumber}
          totalResults={totalFlights}
          hideConnectedFilter={true}
          hideInternalFilters={true}
          readOnly={readOnly}
        />
        <RoutePager
          page={PageNumber}
          totalPages={totalPages}
          onPageChange={setPageNumber}
        />
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    isLoadingFlights,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
    showMobileFilters,
  ]);

  const filterSidebar = (
    <div
      className={`w-[220px] flex-shrink-0 ${hasSearched ? "hidden md:block" : "hidden"}`}
    >
      <div className="sticky top-4">
        {/* Breadcrumb — outside card */}
        <div className="flex items-center gap-3 mb-4 whitespace-nowrap">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors flex-shrink-0"
            onClick={() => {
              handleClearSearch();
              onBack?.();
            }}
          >
            <svg
              className="w-4 h-4 text-[#1F2937]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
          <span className="text-gray-400 text-sm">&gt;</span>
          <span className="text-[#1F2937] text-sm font-medium">Flight No.</span>
        </div>

        {/* Filter card */}
        <div className="bg-white rounded-2xl shadow-sm p-5 min-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[#1F2937] text-base font-bold">Filters</p>
            <button
              type="button"
              className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              title="Reset filters"
              onClick={() => handleStopsFilterChange("none")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p className="text-[#9CA3AF] text-xs mb-4">
            Showing {totalFlights || 0} results
          </p>
          <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
          <div className="flex flex-col gap-3">
            {(["none", "one", "two"] as const).map((val) => {
              const labels: Record<string, string> = {
                none: "Non-stop",
                one: "1 Stop",
                two: "2 Stops",
              };
              return (
                <label
                  key={val}
                  className="flex items-center cursor-pointer group"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="stops-flight"
                      checked={stopsFilter === val}
                      onChange={() => handleStopsFilterChange(val)}
                      className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                    />
                    <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                      {labels[val]}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      {/* Tabs + Form — always default style, full width (no layout change on search) */}
      <div className="relative pt-4">
        <div className="relative z-10">{tabButtons}</div>
        <div className="relative z-20 bg-white shadow-xl p-3 overflow-visible rounded-2xl rounded-tl-none">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
          <p className="text-[#1F2937] text-sm font-medium">
            Search by Flight Number
          </p>
          <div className="flex items-center gap-3 shrink-0 ml-auto">
            <button
              className="flex items-center gap-1 text-[#6B7280] hover:text-[#4B5563] transition-colors"
              onClick={handleClearSearch}
              type="button"
            >
              <i className="st-close text-xs"></i>
              <span className="text-xs">Clear</span>
            </button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFFBEB] border border-[#F8F8F8] rounded-lg hover:bg-[#FEF3C7] transition-colors"
              onClick={handleLastSearchClick}
              type="button"
            >
              <i className="st-previous text-[#192227] text-xs"></i>
              <span className="font-semibold text-[#192227] text-xs">
                Last Search
              </span>
            </button>
          </div>
        </div>
        {/* Fields in separate bordered containers */}
        <div className="flex flex-col md:flex-row md:items-stretch gap-4 mb-3 overflow-visible">
          {/* Flight No. field */}
          <div className="relative flex-[2] p-3 border border-[#E5E7EB] rounded-xl">
            <label className="flex items-center gap-1.5 mb-1 text-xs text-[#6B7280]">
              <i className="st-flight text-[#6B7280] text-[11px]"></i>
              Flight No
            </label>
            <div className="relative" ref={flightFieldRef}>
              <input
                type="text"
                value={flightNumber}
                autoComplete="off"
                onChange={(e) => {
                  setFlightNumber(e.target.value);
                  setShowFlightSuggestions(true);
                  setErrors((prev) => ({
                    ...prev,
                    flightNumber: undefined,
                    apiResponse: undefined,
                  }));
                }}
                onFocus={() => {
                  setShowFlightSuggestions(true);
                }}
                placeholder="Select or search flight number"
                className="w-full pr-12 py-1 bg-transparent border-0 shadow-none appearance-none text-sm text-[#1F2937] font-medium placeholder-[#9CA3AF] focus:outline-none focus:ring-0"
              />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {flightNumber && (
                  <button
                    type="button"
                    onClick={() => {
                      setFlightNumber("");
                      setShowFlightSuggestions(true);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    aria-label="Clear flight number"
                  >
                    <i className="st-close text-xs"></i>
                  </button>
                )}
                <i className="st-arrow-down text-[10px] text-gray-400 pointer-events-none px-1"></i>
              </div>

              {showFlightSuggestions && (
                <ul className="absolute left-0 right-0 top-full mt-2 max-h-60 overflow-auto bg-white rounded-lg shadow-2xl border border-gray-200 z-50 py-1">
                  {isLoadingFlightNumbers ? (
                    <li className="px-4 py-2 text-sm text-gray-400">Loading…</li>
                  ) : flightNumberOptions.length === 0 ? (
                    <li className="px-4 py-2 text-sm text-gray-400">
                      No flight numbers found
                    </li>
                  ) : (
                    flightNumberOptions.map((opt) => (
                      <li
                        key={opt.value}
                        onClick={() => {
                          setFlightNumber(opt.value);
                          setShowFlightSuggestions(false);
                          setErrors((prev) => ({
                            ...prev,
                            flightNumber: undefined,
                            apiResponse: undefined,
                          }));
                          // Auto-open the date picker after selecting
                          setTimeout(() => setIsCalendarOpen(true), 300);
                        }}
                        className={`px-4 py-2.5 text-sm cursor-pointer ${
                          opt.value === flightNumber
                            ? "bg-[#2563EB] text-white"
                            : "text-gray-700 hover:bg-[#DBEAFE]"
                        }`}
                      >
                        {opt.label}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            {errors.flightNumber && (
              <p className="text-red-500 text-[11px] mt-0.5">{errors.flightNumber}</p>
            )}
          </div>

          {/* Date field */}
          <DateRangeCalendar
            selectedDates={selectedDates}
            datesInRange={datesInRange}
            isCalendarOpen={isCalendarOpen}
            setIsCalendarOpen={setIsCalendarOpen}
            onDateChange={handleDateChange}
            onSaveDates={handleSaveDates}
            onClearDates={clearAllDates}
            error={errors.date}
          />
        </div>
        <div className="flex justify-stretch md:justify-end">
        <button
          type="button"
          className={`w-full md:w-auto px-8 py-3 rounded-xl text-[#192227] text-center bg-[#FBBF24] hover:bg-[#F59E0B] text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors ${
            isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
          }`}
          onClick={handleSearchClick}
          disabled={isLoadingFlights}
        >
          {isLoadingFlights ? (
            <>
              <div className="w-5 h-5 border-2 border-[#192227] border-t-transparent rounded-full animate-spin"></div>
              <span>Searching...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Find Your Flight
            </>
          )}
        </button>
        </div>
        </div>
      </div>

      {/* Search results with filter sidebar — rendered below the search area */}
      <div className="flex gap-4 pt-2 md:pt-8">
        {filterSidebar}
        <div className="flex-1 min-w-0">{searchResults}</div>
      </div>
    </div>
  );
};

export default SearchFlightsNumber;
